import React, { useState } from 'react';
import { useOwnerDetails } from 'api/owners';
import { PayoutMethodTypes } from 'api/payoutMethods';
import TempInfo from './TempInfo';
import Table from 'components/Table';
import {
  Button,
  Typography,
  Spin,
  Collapse,
  IconButton,
  Tag,
  Spinner,
  Tooltip,
  Link,
} from 'ui-kit';
import AddPhoneModal from './AddPhoneModal';
import AddPayoutMethodModal from './AddPayoutMethodModal';
import EditPayoutMethodModal from './EditPayoutMethodModal';
import { Edit, Trash2 } from 'react-feather';
import { PayoutMethod } from 'api/types/payout';
import DeletePayoutMethodModal from './DeletePayoutMethodModal';
import StorePayoutMethodsForm from './StorePayoutMethodsForm';
import './PayoutMethods.scss';
import { PaymentPlan } from 'api/types/owner';
import { hasMinimumPlan } from 'utils/owner';
import { useSignedUser } from 'api/auth';

const getPayoutMethodType = (type) => {
  switch (type) {
    case PayoutMethodTypes.STRIPE:
      return 'Stripe';
    case PayoutMethodTypes.PAYPAL:
      return 'Paypal';
    default:
      return type;
  }
};

const PayoutMethods = () => {
  const { data: user } = useSignedUser();
  const { data, isLoading, invalidate } = useOwnerDetails(user?.owner_id);
  const { plan } = data || {};
  const [showPhoneNumberModal, setShowPhoneNumberModal] = useState(false);
  const [showAddPayoutMethodModal, setShowAddPayoutMethodModal] = useState(false);
  const [showEditPayoutMethodModal, setShowEditPayoutMethodModal] = useState(false);
  const [showDeletePayoutMethodModal, setShowDeletePayoutMethodModal] = useState(false);
  const [selectedPayoutMethod, setSelectedPayoutMethod] = useState<PayoutMethod | null>(null);
  const handleAddPayoutMethod = () => {
    if (data?.phone === null || data?.phone === '') {
      setShowPhoneNumberModal(true);
    } else {
      setShowAddPayoutMethodModal(true);
    }
  };
  const handleSubmitPhone = () => {
    setShowPhoneNumberModal(false);
    setShowAddPayoutMethodModal(true);
  };

  const openEditPayoutMethodModal = (payoutMethodId) => {
    setSelectedPayoutMethod(data?.payoutMethods.find((pm) => pm.id === payoutMethodId) || null);
    setShowEditPayoutMethodModal(true);
  };

  const closeEditPayoutMethodModal = () => {
    setSelectedPayoutMethod(null);
    setShowEditPayoutMethodModal(false);
    invalidate();
  };

  const openDeletePayoutMethodModal = (payoutMethodId) => {
    setSelectedPayoutMethod(data?.payoutMethods.find((pm) => pm.id === payoutMethodId) || null);
    setShowDeletePayoutMethodModal(true);
  };

  const closeDeletePayoutMethodModal = () => {
    setSelectedPayoutMethod(null);
    setShowDeletePayoutMethodModal(false);
    invalidate();
  };
  // TODO: there's some issue when no payout methods are present that causes the field to not be camel cased properly
  const canAddPayoutMethod =
    data?.payoutMethods === undefined ||
    data?.payoutMethods?.filter((pm) => pm.type === PayoutMethodTypes.STRIPE).length === 0 ||
    hasMinimumPlan(plan, PaymentPlan.ProPlus);

  if (user?.is_cohost) {
    return null;
  }

  return (
    <Spin spinning={isLoading}>
      <TempInfo payoutType="STRP" />
      <div className="PayoutMethods_collapse">
        <Collapse
          header={() => <Typography variant="h6">Payout Methods</Typography>}
          initialCollpase={false}
        >
          <div className="PayoutMethods_collapse-content">
            <Table
              className="PayoutMethods_collapse-table"
              dataSource={data?.payoutMethods}
              columns={[
                {
                  title: 'Nickname',
                  dataIndex: 'nickname',
                  key: 'nickname',
                  render: (nickname, record) => (
                    <div className="nickname-cell">
                      {nickname}&nbsp;
                      {record.isDefault && <Tag color="secondary" text="Default" />}
                    </div>
                  ),
                },
                {
                  title: 'Type',
                  dataIndex: 'type',
                  key: 'type',
                  render: (text) => getPayoutMethodType(text),
                },
                {
                  title: 'Status',
                  dataIndex: 'isActive',
                  key: 'status',
                  render: (isActive) => (isActive ? 'Active' : 'Inactive'),
                },
                {
                  title: 'Actions',
                  dataIndex: 'id',
                  key: 'id',
                  render: (id) => (
                    <>
                      <IconButton onClick={() => openEditPayoutMethodModal(id)}>
                        <Edit />
                      </IconButton>
                      <IconButton onClick={() => openDeletePayoutMethodModal(id)}>
                        <Trash2 />
                      </IconButton>
                    </>
                  ),
                },
              ]}
              pagination={false}
            />
            <Tooltip
              title={
                !canAddPayoutMethod ? (
                  <div className="PayoutMethods_button-tooltip">
                    Only Pro Plus users can add more than one Stripe payout method.
                    <Link color="textLight" to="/pricing/" className="pricing-link">
                      Upgrade now
                    </Link>
                  </div>
                ) : (
                  ''
                )
              }
            >
              <div className="PayoutMethods_collapse-button-wrapper">
                <Button
                  className="PayoutMethods_collapse-button"
                  variant="contained"
                  onClick={handleAddPayoutMethod}
                  disabled={!canAddPayoutMethod}
                  title="Tooltip Text"
                >
                  Add Payout Method
                </Button>
              </div>
            </Tooltip>
          </div>
        </Collapse>
      </div>
      {hasMinimumPlan(plan, PaymentPlan.ProPlus) && (
        <div className="PayoutMethods_collapse">
          <Collapse header={() => <Typography variant="h6">Store Payout Methods</Typography>}>
            {isLoading ? (
              <Spinner />
            ) : (
              <StorePayoutMethodsForm stores={data?.stores} payoutMethods={data?.payoutMethods} />
            )}
          </Collapse>
        </div>
      )}
      <AddPhoneModal
        visible={showPhoneNumberModal}
        onClose={() => setShowPhoneNumberModal(false)}
        onSubmit={handleSubmitPhone}
      />
      <AddPayoutMethodModal
        visible={showAddPayoutMethodModal}
        handleClose={() => setShowAddPayoutMethodModal(false)}
      />
      <EditPayoutMethodModal
        payoutMethod={selectedPayoutMethod}
        visible={showEditPayoutMethodModal}
        handleClose={closeEditPayoutMethodModal}
      />
      <DeletePayoutMethodModal
        payoutMethod={selectedPayoutMethod}
        allPayoutMethods={data.payoutMethods}
        visible={showDeletePayoutMethodModal}
        handleClose={closeDeletePayoutMethodModal}
        invalidate={invalidate}
      />
    </Spin>
  );
};

export default PayoutMethods;
